import React, { useState, useEffect } from 'react';
import useManageWeeklyShip from './useManageWeeklyShip';
import useShipDate from '../setting/shipDate/useShipDate';
import { formatDate } from '../../utils/dateUtils';
import ImageModal from './ImageModel';

// "หาเจ้าของจากรูปแกะกล่อง" — gallery of boxes whose contents were photographed at the
// Thai warehouse but that are NOT yet matched to an order. The operator eyeballs the
// unpack photo against order images, then goes to the match page to link it.
const WeeklyShipUnpackMatch = () => {
  const [shipDateId, setShipDateId] = useState('all');
  const [shop, setShop] = useState('all');
  const [noTrackingOnly, setNoTrackingOnly] = useState(false);
  const [imageSize, setImageSize] = useState(220); // px — drives both tile size and column count
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [modalSrc, setModalSrc] = useState('');

  const { getUnpackUnmatched, isLoading } = useManageWeeklyShip();
  const { shipDates, getShipDates } = useShipDate({ manage: 'auto' });

  useEffect(() => {
    getShipDates();
  }, []);

  // Reload whenever a filter changes.
  useEffect(() => {
    let active = true;
    (async () => {
      const data = await getUnpackUnmatched(shipDateId, noTrackingOnly, shop);
      if (active) {
        setRows(data);
        setLoaded(true);
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipDateId, shop, noTrackingOnly]);

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-4">หาเจ้าของจากรูปแกะกล่อง</h1>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-x-6 gap-y-3 mb-4">
        <div className="flex flex-col">
          <label className="text-sm text-gray-600">รอบเรือ</label>
          <select
            className="px-3 py-1.5 border border-gray-400 rounded-md"
            value={shipDateId}
            onChange={(e) => setShipDateId(e.target.value)}
          >
            <option value="all">ทั้งหมด</option>
            {shipDates && shipDates.map((d) => (
              <option key={d.uuid || d.id} value={d.id}>
                {`${formatDate(d.shipDate)}${d.isAir ? ' - เครื่องบิน' : ''}`}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col">
          <label className="text-sm text-gray-600">ร้าน</label>
          <select
            className="px-3 py-1.5 border border-gray-400 rounded-md"
            value={shop}
            onChange={(e) => setShop(e.target.value)}
          >
            <option value="all">ทั้งหมด</option>
            <option value="tama">tama</option>
            <option value="smile">smile</option>
          </select>
        </div>

        <label className="flex items-center gap-2 select-none pb-1.5">
          <input type="checkbox" checked={noTrackingOnly} onChange={(e) => setNoTrackingOnly(e.target.checked)} />
          <span>เฉพาะไม่มีเลขพัสดุ</span>
        </label>

        <div className="flex flex-col">
          <label className="text-sm text-gray-600">ขนาดรูป</label>
          <input
            type="range"
            min={120}
            max={480}
            step={20}
            value={imageSize}
            onChange={(e) => setImageSize(Number(e.target.value))}
            className="w-40 cursor-pointer"
          />
        </div>

        <span className="text-sm text-gray-500 pb-1.5">{rows.length} รายการ</span>
      </div>

      {isLoading && <p className="text-gray-500">กำลังโหลด...</p>}

      {loaded && !isLoading && rows.length === 0 && (
        <p className="text-gray-500">ไม่มีรูปแกะกล่องที่ยังไม่จับคู่</p>
      )}

      {/* Responsive grid: tile min-width = imageSize, so bigger images = fewer columns. */}
      <div
        className="grid gap-4"
        style={{ gridTemplateColumns: `repeat(auto-fill, minmax(min(${imageSize}px, 100%), 1fr))` }}
      >
        {rows.map((row) => (
          <div key={row.id} className="border border-gray-300 rounded-md p-2">
            {row.unpackImageUrl && (
              <img
                src={row.unpackImageUrl}
                alt="unpack"
                className="w-full object-contain cursor-pointer bg-gray-50"
                style={{ height: imageSize }}
                onClick={() => setModalSrc(row.unpackImageUrl)}
              />
            )}
            <div className="mt-2 text-xs leading-snug">
              <p><span className="font-semibold">Box No:</span> {row.boxNo ?? '-'}</p>
              <p><span className="font-semibold">Box Name:</span> {row.boxName ?? '-'}</p>
              <p><span className="font-semibold">Tracking:</span> {row.trackingJp ?? '-'}</p>
              <p><span className="font-semibold">Shop:</span> {row.shop ?? '-'}</p>
            </div>
          </div>
        ))}
      </div>

      {modalSrc && <ImageModal src={modalSrc} onClose={() => setModalSrc('')} />}
    </div>
  );
};

export default WeeklyShipUnpackMatch;
